import type { ClientPollCreateRequest } from '@DigitaleDoerfer/digitale-doerfer-api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, type FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

import { CheckIcon } from '@/assets';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import type { PollFeature } from '@/features/feature-management/types/pollFeature';
import { BottomModal } from '@/features/popup/components/BottomModal';
import BottomSheetKeyboardAwareScrollView from '@/features/popup/components/BottomSheetKeyboardAwareScrollView';
import { PollOptionsFrom } from '@/features/post-editor/components/poll/options/PollOptionsForm';
import { EMPTY_POLL_CREATE } from '@/features/post-editor/constants/poll';
import { createPollCreateSchema } from '@/features/post-editor/schemas/poll';
import type { Poll, PollCreate } from '@/features/post-editor/types/poll';
import { convertPollCreateToClientPoll } from '@/features/post-editor/utils/poll';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { FormDatePicker } from '@/shared/components/forms/FormDatePicker';
import { FormGroup } from '@/shared/components/forms/FormGroup';
import { FormSwitch } from '@/shared/components/forms/FormSwitch';
import { FormTextInput } from '@/shared/components/forms/FormTextInput';
import { DateMode } from '@/shared/types/form';
import { isWeb } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import { dayjs, TOMORROW } from '@/time';

const styles = StyleSheet.create({
  container: { padding: 12 },
  group: { paddingBottom: 24 },
  switch: { paddingVertical: 12 }
});

type Props = {
  poll?: Poll;
  isVisible?: boolean;
  onChange: (poll?: ClientPollCreateRequest) => void;
  onClose: (isDirty: boolean, callback: () => void) => void;
};

export const PollForm: FC<Props> = ({ poll, isVisible, onChange, onClose }) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.POST_EDITOR, {
    keyPrefix: 'components.poll.form'
  });
  const { getFeature } = useFeature();

  const pollFeature = getFeature<PollFeature>(FeaturesEnum.Poll);
  const featureValues = pollFeature.featureValues;

  const pollCreateSchema = createPollCreateSchema({
    t,
    pollFeatureValues: featureValues
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
    clearErrors
  } = useForm<PollCreate>({
    resolver: zodResolver(pollCreateSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    values: { ...EMPTY_POLL_CREATE, ...poll }
  });

  // Fix: zod superfine https://github.com/colinhacks/zod/discussions/2220
  useEffect(() => {
    if (isValid) clearErrors();
  }, [clearErrors, isValid]);

  const handleClose = useCallback(() => {
    onClose(isDirty, reset);
  }, [isDirty, onClose, reset]);

  const handleSubmitPollChanges = useCallback(
    (pollCreate: PollCreate) =>
      onChange(convertPollCreateToClientPoll(pollCreate)),
    [onChange]
  );

  return (
    <BottomModal
      isVisible={isVisible}
      title={t('poll')}
      titleAlign="left"
      onClose={handleClose}
      enablePanDownToClose={false}
      closeButtonMode="outlined"
      endButtons={[
        {
          icon: CheckIcon,
          disabled: !isValid,
          onPress: () => {
            handleSubmit(handleSubmitPollChanges)();
          },
          mode: 'contained',
          theme: {
            colors: {
              surfaceVariant: theme.colors.error,
              primary: theme.colors.onPrimary
            }
          }
        }
      ]}
      keyboardShouldPersistTaps="handled"
      onBackdropPress={handleClose}
      maxDynamicContentSizeRatio={isWeb ? undefined : 0.7}
      fillScrollContent={false}
      stickyHeader={!isWeb}
      scrollComponent={BottomSheetKeyboardAwareScrollView}
    >
      <View style={styles.container}>
        <FormGroup style={styles.group}>
          <FormTextInput
            control={control}
            name="text"
            mode="outlined"
            label={t('question.title')}
            multiline={!isWeb}
            maxLength={featureValues.maxNumberCharsPollText}
          />
        </FormGroup>

        <PollOptionsFrom
          control={control}
          maxLength={featureValues.maxNumberCharsPollOptionText}
        />

        <FormGroup label={t('endsAt')} style={styles.group}>
          <FormDatePicker
            control={control}
            name="expirationTime"
            mode={DateMode.DATE}
            label={t('date.title')}
            placeholder={t('date.placeholder')}
            validRange={{
              startDate: TOMORROW,
              endDate: dayjs()
                .add(-1, 'day')
                .add(featureValues.maxPollDurationMilliseconds, 'millisecond')
                .toDate()
            }}
          />
          <FormDatePicker
            control={control}
            name="expirationTime"
            mode={DateMode.TIME}
            label={t('time.title')}
            placeholder={t('time.placeholder')}
          />
        </FormGroup>
        <FormSwitch
          label={t('allowMultiselect')}
          control={control}
          name="multipleChoice"
          style={styles.switch}
          // Todo: needed when device is in dark mode. Remove once react native paper fixes dark mode colors
          ios_backgroundColor={theme.colors.onSurfaceDisabled}
        />
      </View>
    </BottomModal>
  );
};
